import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Box, Loader, useThemeName, Stack, Heading, Card, Text, Divider, Bleed, Badge, List, Alert, Inline, Columns, Column, ContentBlock } from "braid-design-system";
import { useContractBalancesForHirerBalanceQuery, useHirerAccountForHirerBalanceQuery, useOrderSummaryDetailsQuery, useHirerAccountQuery, useGetContractBalancesQuery, useGetOrderSummaryQuery } from "./graphql-types.chunk.mjs";
import { TrackingProvider, HirerBalancePresentation } from "./styles/HirerContractBalance/HirerBalancePresentation.mjs";
import { ErrorAlert } from "./styles/HirerContractBalance/components/ErrorAlert/ErrorAlert.mjs";
import { PermissionsProvider } from "./styles/HirerContractBalance/components/BalanceCarousel/BalanceCarouselWrapper.mjs";
import * as React from "react";
import { useContext, createContext, useRef, useEffect, useCallback, useState } from "react";
import { useTranslations, useLanguage } from "@vocab/react";
import { formatMinorUnits } from "@seek/currency";
import { translations } from "./HirerOrderSummary/components/.vocab/index.mjs";
import { OrderTableRow } from "./styles/HirerOrderSummary/components/OrderTableRow/OrderTableRow.mjs";
import { TaxSuffix } from "@seek/tax-suffix";
import { format, parseISO } from "date-fns";
import { SubTotal } from "./styles/HirerOrderSummary/components/SubTotal/SubTotal.mjs";
import { ShoppingCartContext } from "@seek/shopping-cart-provider";
import { locales } from "@seek/melways-sites";
import { translations as translations$1 } from "./Checkout/.vocab/index.mjs";
import { getSupportedPaymentMethodsForCaja, PaymentsForCaja } from "@seek/payments-widget";
import { checkIsPaymentsReturnUrl } from "@seek/payments-widget";
import { translations as translations$2 } from "./Checkout/components/.vocab/index.mjs";
import { HirerPromoCodesBanner, usePromoCodesBanner } from "@seek/hirer-promo-codes-banner";
import { HirerPromoCodesInput } from "@seek/hirer-promo-codes-input";
const mapGraphqlToContractBalanceProp = (contractBalance) => {
  const currentContract = (contractBalance.contracts || [])[0];
  return {
    ...contractBalance,
    expiresSoon: currentContract == null ? void 0 : currentContract.expiry.expiresSoon
  };
};
const getPricingModel = (pricingModelFromHirerAccount, hirerAccountError) => {
  if (hirerAccountError || !pricingModelFromHirerAccount) {
    return "HORIZON";
  }
  return pricingModelFromHirerAccount;
};
const HirerContractBalance = ({
  locale,
  advertiserNation,
  canCreateJobs,
  tracking = () => {
  },
  ...otherProps
}) => {
  var _a;
  const {
    loading: contractBalanceLoading,
    error: contractBalanceError,
    data: contractBalanceData
  } = useContractBalancesForHirerBalanceQuery({
    variables: { advertiserNation }
  });
  const {
    loading: hirerAccountLoading,
    error: hirerAccountError,
    data: hirerAccountData
  } = useHirerAccountForHirerBalanceQuery();
  if (contractBalanceLoading || hirerAccountLoading) {
    return /* @__PURE__ */ jsx(Box, { "data-testid": "Loader", children: /* @__PURE__ */ jsx(Loader, { size: "xsmall" }) });
  }
  if (contractBalanceError) {
    return /* @__PURE__ */ jsx(ErrorAlert, {});
  }
  const pricingModel = getPricingModel(
    (_a = hirerAccountData == null ? void 0 : hirerAccountData.hirerAccount) == null ? void 0 : _a.pricingModel,
    hirerAccountError
  );
  const contractBalances = ((contractBalanceData == null ? void 0 : contractBalanceData.contractBalances) ?? []).map(
    (x) => mapGraphqlToContractBalanceProp(x)
  );
  return /* @__PURE__ */ jsx(TrackingProvider, { context: tracking, children: /* @__PURE__ */ jsx(PermissionsProvider, { canCreateJobs: canCreateJobs ?? false, children: /* @__PURE__ */ jsx(
    HirerBalancePresentation,
    {
      ...otherProps,
      contractBalances,
      pricingModel,
      locale
    }
  ) }) });
};
var Locales = /* @__PURE__ */ ((Locales2) => {
  Locales2["AU"] = "AU";
  Locales2["NZ"] = "NZ";
  return Locales2;
})(Locales || {});
const defaultLogger$1 = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const LoggerContext = React.createContext(defaultLogger$1);
const LoggerProvider = ({ children, context }) => /* @__PURE__ */ jsx(
  LoggerContext.Provider,
  {
    value: {
      ...defaultLogger$1,
      ...context
    },
    children
  }
);
const useLogger = () => {
  const { warn, error, info } = useContext(LoggerContext);
  return { warn, error, info };
};
const OrderSummaryLayout = ({
  children,
  paddingStyles,
  title,
  prefixContent
}) => {
  const theme = useThemeName();
  return /* @__PURE__ */ jsx(Box, { background: "surface", ...paddingStyles, children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    Boolean(title) ? /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", weight: "regular", children: title }) : null,
    prefixContent,
    theme === "apac" ? /* @__PURE__ */ jsx(Box, { children }) : /* @__PURE__ */ jsx(Card, { children })
  ] }) });
};
function assert(condition, msg) {
  if (!condition) {
    throw new Error(msg);
  }
}
const alertMessageResponses = {
  costThisInvoice: "the cost of this...invoice",
  costTheOutstandingBalance: "the cost of the...outstanding amount",
  weWillDeduct: "we will deduct..."
};
const getAmexSurchargeAmount = (orderSummary, surchargeToApply) => {
  var _a, _b;
  return ((_b = (_a = orderSummary.surcharges.find(
    (surcharge) => surcharge.surchargeType === surchargeToApply
  )) == null ? void 0 : _a.amount) == null ? void 0 : _b.value) ?? 0;
};
const isUpsellProductLineItem = (lineItem) => lineItem.product.productType === "AD_BUDGET";
const isLineItemFullyDrawdown = (lineItem) => {
  var _a;
  return (((_a = lineItem.price.netPrice) == null ? void 0 : _a.value) ?? 0) === lineItem.drawdownItems.reduce((aggr, drawdown) => aggr + drawdown.amount, 0);
};
const getNetPriceFromLineItem = (lineItem) => {
  var _a;
  return ((_a = lineItem.price.netPrice) == null ? void 0 : _a.value) ?? 0;
};
const mapLineItem = (lineItem) => {
  var _a;
  const netPrice = getNetPriceFromLineItem(lineItem);
  function getTitle(item) {
    if (item.product.productType === "JOB_AD_EDIT") {
      return "Ad edits";
    }
    if (item.product.productType === "JOB_AD_UPGRADE") {
      return "Upgrade to StandOut ad";
    }
    return lineItem.product.name ?? "";
  }
  return {
    state: "fullPrice",
    netPrice,
    productType: lineItem.product.productType,
    retailPrice: ((_a = lineItem.price.retailPrice) == null ? void 0 : _a.value) ?? 0,
    title: getTitle(lineItem),
    currency: lineItem.price.retailPrice.currency
  };
};
const getOutstandingAmount = (lineItem, amount) => (lineItem.state === "fullPrice" ? lineItem.netPrice : lineItem.outstandingAmount) - amount;
const getPayableLineItem = (lineItem, amount, contractName) => ({
  title: lineItem.title,
  productType: lineItem.productType,
  state: "outstanding",
  paidContractName: contractName,
  currency: lineItem.currency,
  outstandingAmount: getOutstandingAmount(lineItem, amount)
});
const getKeyFromDrawdown = (drawdown) => drawdown.drawdownContractDetails.contractId ?? drawdown.drawdownContractDetails.expiryDate ?? "";
const mapDrawdownItem = (lineItem, drawdown, isUpsell) => {
  var _a, _b, _c;
  const {
    drawdownContractDetails: {
      remainingValue,
      currentValue,
      expiryDate,
      contractName,
      contractType
    }
  } = drawdown;
  return {
    key: getKeyFromDrawdown(drawdown),
    lineItems: [lineItem],
    contractType,
    contractName: contractName ?? contractType,
    remainingBalance: ((_a = remainingValue == null ? void 0 : remainingValue.dollars) == null ? void 0 : _a.value) ?? 0,
    startingBalance: ((_b = currentValue == null ? void 0 : currentValue.dollars) == null ? void 0 : _b.value) ?? 0,
    currency: ((_c = currentValue == null ? void 0 : currentValue.dollars) == null ? void 0 : _c.currency) ?? "",
    outstandingBalance: 0,
    ...expiryDate && { expiryDate },
    ...isUpsell && {
      alertMessage: alertMessageResponses.weWillDeduct
    }
  };
};
const filterOutZeroDrawdowns = (contractBalanceItem) => contractBalanceItem.startingBalance - contractBalanceItem.remainingBalance > 0;
const fillOutstandingInBalanceItem = (contractBalanceItem) => {
  const totalCost = contractBalanceItem.lineItems.reduce(
    (total, lineItem) => total + (lineItem.state === "outstanding" ? lineItem.outstandingAmount : lineItem.netPrice),
    0
  );
  const outstandingBalance = Math.max(
    0,
    totalCost - contractBalanceItem.startingBalance
  );
  assert(
    contractBalanceItem.startingBalance - contractBalanceItem.remainingBalance === totalCost - outstandingBalance,
    `contractBalance item calculation is not verified, contractBalanceItem: ${JSON.stringify(
      contractBalanceItem
    )}, totalCost: ${totalCost}, outstandingBalance: ${outstandingBalance}`
  );
  return { ...contractBalanceItem, outstandingBalance };
};
const fillExhaustAlertInBalanceItem = (contractBalanceItem, _index, contractBalanceItems) => {
  const hasOutstandingAmountLineItem = contractBalanceItem.lineItems.some(
    (lineItem) => lineItem.state === "outstanding"
  );
  if (!hasOutstandingAmountLineItem) {
    return contractBalanceItem;
  }
  const firstContractBalanceItem = contractBalanceItems.find(
    (budgetItem) => budgetItem.key !== contractBalanceItem.key
  );
  assert(
    firstContractBalanceItem,
    "It is not possible to have only one contract to drawdown a paying line item"
  );
  return {
    ...contractBalanceItem,
    ...hasOutstandingAmountLineItem && {
      alertMessage: alertMessageResponses.costTheOutstandingBalance
    }
  };
};
const getAndGroupContractBalanceItems = (lineItems, isUpsell, mapSingleLineItem) => {
  const contractBalanceItems = lineItems.reduce((drawdowns, lineItem) => {
    let contractBalanceItemsToPresent = drawdowns;
    let lineItemPresentation = mapSingleLineItem(lineItem);
    lineItem.drawdownItems.forEach((drawdown) => {
      var _a, _b, _c, _d;
      const key = getKeyFromDrawdown(drawdown);
      const existedDrawdown = drawdowns.find(
        (drawdownItem) => drawdownItem.key === key
      );
      if (existedDrawdown) {
        existedDrawdown.startingBalance = Math.max(
          existedDrawdown.startingBalance,
          ((_b = (_a = drawdown.drawdownContractDetails.currentValue) == null ? void 0 : _a.dollars) == null ? void 0 : _b.value) ?? 0
        );
        existedDrawdown.remainingBalance = Math.min(
          existedDrawdown.remainingBalance,
          ((_d = (_c = drawdown.drawdownContractDetails.remainingValue) == null ? void 0 : _c.dollars) == null ? void 0 : _d.value) ?? 0
        );
        existedDrawdown.lineItems.push(lineItemPresentation);
      } else {
        contractBalanceItemsToPresent = [
          ...contractBalanceItemsToPresent,
          mapDrawdownItem(lineItemPresentation, drawdown, isUpsell)
        ];
      }
      lineItemPresentation = getPayableLineItem(
        lineItemPresentation,
        drawdown.amount,
        drawdown.drawdownContractDetails.contractName ?? drawdown.drawdownContractDetails.contractType
      );
    });
    return contractBalanceItemsToPresent;
  }, []);
  return contractBalanceItems.filter(filterOutZeroDrawdowns).map(fillOutstandingInBalanceItem).map(fillExhaustAlertInBalanceItem);
};
function getPaidAmountAndContractFromLineItems(lineItem) {
  return lineItem.drawdownItems.reduce(
    ([total], drawdown) => [
      total + drawdown.amount,
      drawdown.drawdownContractDetails.contractName ?? drawdown.drawdownContractDetails.contractType
    ],
    [0, ""]
  );
}
const getPayableItems = (lineItems, mapSingleLineItem) => lineItems.map((lineItem) => {
  const lineItemPresentation = mapSingleLineItem(lineItem);
  const [paid, contractName] = getPaidAmountAndContractFromLineItems(lineItem);
  return paid > 0 ? getPayableLineItem(lineItemPresentation, paid, contractName) : lineItemPresentation;
}).filter(
  (lineItem) => lineItem.state === "fullPrice" || lineItem.outstandingAmount !== 0
);
const mapOrderSummaryToOrderSummaryPresentation = (orderSummary, surchargeToApply) => {
  assert(orderSummary != null, "orderSummary is null");
  const amexSurchargeAmount = getAmexSurchargeAmount(
    orderSummary,
    surchargeToApply
  );
  const total = (orderSummary.total.value ?? 0) + amexSurchargeAmount;
  const upsellLineItems = orderSummary.lineItems.filter(
    isUpsellProductLineItem
  );
  const nonUpsellLineItems = orderSummary.lineItems.filter(
    (lineItem) => !isUpsellProductLineItem(lineItem)
  );
  assert(
    upsellLineItems.length <= 1,
    "When upsell, there should be only 1 line item containing upsell product"
  );
  assert(
    upsellLineItems.length === 0 || nonUpsellLineItems.length <= 1,
    "When upsell, there should be at most one Ad in line items"
  );
  assert(
    upsellLineItems.length === 0 || nonUpsellLineItems.filter(isLineItemFullyDrawdown).length === nonUpsellLineItems.length,
    "When upsell, the Ad line items should be fully drawn down"
  );
  const lineItems = upsellLineItems.length > 0 ? upsellLineItems : orderSummary.lineItems;
  const contractBalanceItems = getAndGroupContractBalanceItems(
    upsellLineItems.length > 0 ? nonUpsellLineItems : lineItems,
    upsellLineItems.length > 0,
    mapLineItem
  );
  return {
    tax: orderSummary.tax,
    subTotal: orderSummary.subTotal.value ?? 0,
    total,
    surcharge: amexSurchargeAmount,
    contractBalanceItems,
    payableLineItems: getPayableItems(lineItems, mapLineItem),
    currency: orderSummary.total.currency
  };
};
const mapCPQAOrderSummaryToCPQAOrderSummaryPresentation = (orderSummary, locale) => {
  var _a, _b, _c, _d, _e;
  assert(orderSummary != null, "orderSummary is null");
  const cpqaLineItem = orderSummary.lineItems.find(
    (item) => {
      var _a2;
      return ((_a2 = item.pricing) == null ? void 0 : _a2.__typename) === "CappedUnitPrice";
    }
  );
  assert(
    ((_a = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _a.__typename) === "CappedUnitPrice" && ((_b = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _b.unitPrice.__typename) === "Price" && ((_c = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _c.cappedPrice.__typename) === "Price",
    "lineItem with pricing is not in expected shape"
  );
  const {
    pricing: {
      unitPrice: { netPrice: unitNetPrice, tax: unitPriceTax },
      cappedPrice: { netPrice: cappedNetPrice, tax: cappedPriceTax },
      maxBillableUnits
    }
  } = cpqaLineItem;
  const unitPrice = (unitPriceTax == null ? void 0 : unitPriceTax.displayRecommendation) === "inclusive" ? unitNetPrice.value + (((_d = unitPriceTax.amount) == null ? void 0 : _d.value) ?? 0) : unitNetPrice.value;
  const formattedUnitNetPrice = formatMinorUnits(unitPrice, {
    currency: unitNetPrice.currency,
    locale,
    dropFraction: true
  });
  const formattedCappedNetPrice = formatMinorUnits(cappedNetPrice.value, {
    currency: cappedNetPrice.currency,
    locale
  });
  const formattedCappedTaxPrice = (cappedPriceTax == null ? void 0 : cappedPriceTax.amount) ? formatMinorUnits(cappedPriceTax.amount.value, {
    currency: cappedPriceTax.amount.currency,
    locale
  }) : void 0;
  return {
    maxBillableUnits,
    unitPrice: {
      formattedPrice: formattedUnitNetPrice,
      tax: (unitPriceTax == null ? void 0 : unitPriceTax.displayRecommendation) ? {
        displayRecommendation: unitPriceTax.displayRecommendation,
        code: unitPriceTax.code
      } : void 0
    },
    cappedPrice: {
      formattedPrice: formattedCappedNetPrice,
      tax: cappedPriceTax && formattedCappedTaxPrice ? {
        code: cappedPriceTax.code,
        formattedPrice: formattedCappedTaxPrice
      } : void 0
    },
    total: cappedNetPrice.value + (((_e = cappedPriceTax == null ? void 0 : cappedPriceTax.amount) == null ? void 0 : _e.value) ?? 0),
    currency: cappedNetPrice.currency,
    tax: cappedPriceTax ?? void 0
  };
};
const OrderSummaryDataContext = createContext({
  trackEventHandler: () => {
  },
  orderSummary: null,
  styles: {},
  language: "en-AU"
});
const OrderSummaryDataProvider = (props) => {
  var _a;
  const {
    orderId,
    advertiserId,
    surcharge,
    onLoading = () => {
    },
    styles = {},
    children,
    hubbleTrackEventHandler = () => {
    },
    language,
    prefixContent
  } = props;
  const logger = useLogger();
  const {
    loading: orderSummaryLoading,
    data,
    error
  } = useOrderSummaryDetailsQuery({
    variables: { orderId, advertiserId },
    fetchPolicy: "network-only"
  });
  const loadingPreviousRef = useRef();
  useEffect(() => {
    const currentLoading = orderSummaryLoading || !data;
    if (currentLoading !== loadingPreviousRef.current) {
      onLoading(!loadingPreviousRef.current);
      loadingPreviousRef.current = !loadingPreviousRef.current;
    }
  }, [orderSummaryLoading, onLoading, data]);
  const WIDGET_NAME = "OrderSummary";
  const trackEventHandlerWithBaseData = useCallback(
    (eventName, eventData, type) => hubbleTrackEventHandler(
      eventName,
      {
        ...eventData,
        // @ts-expect-error Should probably be added as an optional property on the event data type
        widgetName: WIDGET_NAME
      },
      type
    ),
    [hubbleTrackEventHandler]
  );
  if (error) {
    throw error;
  }
  if (orderSummaryLoading || !data) {
    if (!orderSummaryLoading) {
      logger.error(
        `Warning: OrderSummaryDetails loaded but the data is null: (orderId: ${orderId}, advertiserId: ${advertiserId})`,
        {}
      );
    }
    return /* @__PURE__ */ jsx(OrderSummaryLayout, { paddingStyles: styles.padding, children: /* @__PURE__ */ jsx(Box, { role: "loader", children: /* @__PURE__ */ jsx(Loader, { size: "small", delayVisibility: true }) }) });
  }
  if (!data.orderSummary) {
    throw new Error(
      `Order (orderId: ${orderId}, advertiserId: ${advertiserId}) does not exist.`
    );
  }
  return /* @__PURE__ */ jsx(
    OrderSummaryDataContext.Provider,
    {
      value: {
        trackEventHandler: trackEventHandlerWithBaseData,
        orderSummary: data.orderSummary,
        surchargeToApply: (surcharge == null ? void 0 : surcharge.surchargeApplicable) ? ((_a = data.orderSummary.surcharges[0]) == null ? void 0 : _a.surchargeType) ?? void 0 : void 0,
        styles,
        language,
        prefixContent
      },
      children
    }
  );
};
const useOrderSummaryPresentationData = () => {
  const {
    orderSummary,
    surchargeToApply,
    styles: { padding, space },
    language,
    prefixContent
  } = useContext(OrderSummaryDataContext);
  assert(
    orderSummary,
    "orderSummary must be defined in the OrderSummaryDataProvider"
  );
  const orderSummaryPresentation = mapOrderSummaryToOrderSummaryPresentation(
    orderSummary,
    surchargeToApply
  );
  const isCPQASummary = Boolean(
    orderSummary.lineItems.find(
      (item) => {
        var _a;
        return ((_a = item.pricing) == null ? void 0 : _a.__typename) === "CappedUnitPrice";
      }
    )
  );
  const cpqaSummaryPresentation = isCPQASummary ? mapCPQAOrderSummaryToCPQAOrderSummaryPresentation(orderSummary, language) : void 0;
  return {
    orderSummary: orderSummaryPresentation,
    padding,
    space,
    language,
    cpqaOrderSummary: cpqaSummaryPresentation,
    prefixContent
  };
};
const PayableHeader = () => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(
    OrderTableRow,
    {
      left: /* @__PURE__ */ jsx(Text, { weight: "strong", align: "left", children: t("items...") }),
      right: /* @__PURE__ */ jsx(Text, { weight: "strong", align: "right", children: t("cost...") })
    }
  );
};
const Total = ({ total, tax, language, currency }) => {
  const { t } = useTranslations(translations);
  const theme = useThemeName();
  return /* @__PURE__ */ jsx(
    OrderTableRow,
    {
      left: tax ? /* @__PURE__ */ jsx(
        TaxSuffix,
        {
          taxCode: tax.code,
          displayRecommendation: "inclusive",
          locale: language,
          children: /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", children: t("total...") })
        }
      ) : /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", children: t("total...") }),
      right: /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", align: "right", children: formatMinorUnits(total, { currency, locale: language }) })
    }
  );
};
const taxPrefix = {
  inclusive: "incl. ",
  exclusive: "+"
};
const BillableUnitsSummary = () => {
  const { cpqaOrderSummary } = useOrderSummaryPresentationData();
  assert(cpqaOrderSummary, "CPQA order summary should be defined");
  const { maxBillableUnits, unitPrice } = cpqaOrderSummary;
  const taxType = unitPrice.tax ? taxPrefix[unitPrice.tax.displayRecommendation] : void 0;
  const taxSuffix = unitPrice.tax ? `${taxType}${unitPrice.tax.code}` : void 0;
  const costPerApplicantPrice = [unitPrice.formattedPrice, taxSuffix].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(Bleed, { horizontal: "medium", children: /* @__PURE__ */ jsx(Box, { background: "neutralLight", paddingX: "medium", paddingY: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    /* @__PURE__ */ jsx(Badge, { tone: "promote", children: "Premium feature" }),
    /* @__PURE__ */ jsx(Text, { size: "small", children: "Relevant applications" }),
    /* @__PURE__ */ jsxs(List, { size: "small", space: "xsmall", children: [
      /* @__PURE__ */ jsxs(Text, { children: [
        "Receive at least ",
        maxBillableUnits,
        " relevant applications (that claim to have working rights and 2+ years' experience)"
      ] }),
      /* @__PURE__ */ jsxs(Text, { children: [
        "Pay ",
        costPerApplicantPrice,
        " less for each relevant application short of ",
        maxBillableUnits
      ] })
    ] })
  ] }) }) });
};
const CPQASummary = () => {
  const { t } = useTranslations(translations);
  const { padding, language, cpqaOrderSummary } = useOrderSummaryPresentationData();
  assert(cpqaOrderSummary, "CPQA order summary should be defined");
  const { total, currency, tax, cappedPrice } = cpqaOrderSummary;
  const { trackEventHandler, orderSummary: contextOrderSummary } = useContext(
    OrderSummaryDataContext
  );
  const orderId = contextOrderSummary == null ? void 0 : contextOrderSummary.orderId;
  useEffect(() => {
    if (orderId) {
      trackEventHandler(
        "order_summary_displayed",
        {
          orderId
        },
        "view"
      );
    }
  }, [orderId, trackEventHandler]);
  return /* @__PURE__ */ jsx(OrderSummaryLayout, { paddingStyles: padding, title: t("order summary..."), children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(PayableHeader, {}),
      /* @__PURE__ */ jsx(Divider, { weight: "strong" })
    ] }),
    /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Text, { weight: "strong", tone: "secondary", children: "Premium Ad" }),
        right: /* @__PURE__ */ jsx(Text, { weight: "strong", align: "right", tone: "secondary", children: cappedPrice.formattedPrice })
      }
    ),
    cappedPrice.tax && /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Text, { tone: "secondary", children: cappedPrice.tax.code }),
        right: /* @__PURE__ */ jsx(Text, { tone: "secondary", align: "right", children: cappedPrice.tax.formattedPrice })
      }
    ),
    /* @__PURE__ */ jsx(BillableUnitsSummary, {}),
    /* @__PURE__ */ jsx(
      Total,
      {
        total,
        tax,
        language,
        currency
      }
    )
  ] }) });
};
const AlertMessage = ({ message }) => /* @__PURE__ */ jsx(Alert, { tone: "info", children: /* @__PURE__ */ jsx(Text, { children: message }) });
const ProductLineItemContext = createContext(void 0);
const LineItemCost = ({ lineItem, showMinus, language }) => {
  const tone = useContext(ProductLineItemContext);
  if (lineItem.state === "fullPrice" && lineItem.netPrice !== lineItem.retailPrice) {
    return /* @__PURE__ */ jsxs(Inline, { space: "small", align: "right", children: [
      /* @__PURE__ */ jsx(Text, { tone, children: /* @__PURE__ */ jsx("s", { children: formatMinorUnits(lineItem.retailPrice, {
        currency: lineItem.currency,
        locale: language
      }) }) }),
      /* @__PURE__ */ jsxs(Text, { tone, children: [
        showMinus ? `–` : "",
        " ",
        formatMinorUnits(lineItem.netPrice, {
          currency: lineItem.currency,
          locale: language
        })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs(Text, { tone, align: "right", children: [
    showMinus ? `– ` : "",
    formatMinorUnits(
      lineItem.state === "fullPrice" ? lineItem.netPrice : lineItem.outstandingAmount,
      {
        currency: lineItem.currency,
        locale: language
      }
    )
  ] });
};
const ProductLineItem = ({ lineItem, showMinus, showPartial, tone, language }) => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(ProductLineItemContext.Provider, { value: tone, children: /* @__PURE__ */ jsx(
    OrderTableRow,
    {
      left: /* @__PURE__ */ jsxs(Inline, { space: "small", alignY: "center", children: [
        /* @__PURE__ */ jsx(Text, { tone, children: lineItem.title }),
        lineItem.state === "outstanding" && showPartial && /* @__PURE__ */ jsx(Badge, { tone: "neutral", children: t("partial payment...") })
      ] }),
      right: /* @__PURE__ */ jsx(
        LineItemCost,
        {
          lineItem,
          showMinus,
          language
        }
      )
    }
  ) });
};
const HeaderText = ({
  content,
  expiryDate
}) => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(Inline, { space: "xsmall", alignY: "center", children: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Text, { weight: "medium", children: content }),
    expiryDate && /* @__PURE__ */ jsx(Badge, { tone: "neutral", children: t("expires...", {
      date: format(parseISO(expiryDate), "dd/MM/yyyy")
    }) })
  ] }) });
};
const LeftAlignedText = (props) => /* @__PURE__ */ jsx(Text, { ...props, align: "left" });
const RightAlignedText = (props) => /* @__PURE__ */ jsx(Text, { ...props, align: "right" });
const ContractBalanceItem = ({ item }) => {
  const { orderSummary, language } = useOrderSummaryPresentationData();
  const { t } = useTranslations(translations);
  const isUsingStackedContract = orderSummary.contractBalanceItems.length >= 2;
  const showOutstanding = item.outstandingBalance > 0 && item.remainingBalance === 0;
  const theme = useThemeName();
  return /* @__PURE__ */ jsx(Stack, { space: "large", children: /* @__PURE__ */ jsxs(Fragment, { children: [
    item.alertMessage && /* @__PURE__ */ jsx(AlertMessage, { message: t(item.alertMessage) }),
    /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(
        OrderTableRow,
        {
          left: /* @__PURE__ */ jsx(
            HeaderText,
            {
              content: item.contractName,
              expiryDate: isUsingStackedContract ? void 0 : item.expiryDate
            }
          ),
          right: /* @__PURE__ */ jsx(RightAlignedText, { weight: "medium", children: t("balance...") })
        }
      ),
      /* @__PURE__ */ jsx(Divider, { weight: "strong" })
    ] }),
    /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(LeftAlignedText, { weight: "strong", children: t("starting balance...") }),
        right: /* @__PURE__ */ jsx(RightAlignedText, { weight: "strong", children: formatMinorUnits(item.startingBalance, {
          currency: item.currency,
          locale: language
        }) })
      }
    ),
    /* @__PURE__ */ jsx(Box, { marginBottom: "xsmall", children: /* @__PURE__ */ jsx(Stack, { space: "large", children: item.lineItems.map((lineItem, index) => /* @__PURE__ */ jsx(
      ProductLineItem,
      {
        lineItem,
        showMinus: true,
        language
      },
      `lineitem-${index}`
    )) }) }),
    showOutstanding ? /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Heading, { align: "left", level: theme === "apac" ? "3" : "4", children: t("outstanding balance...") }),
        right: /* @__PURE__ */ jsxs(RightAlignedText, { tone: "critical", size: "large", weight: "strong", children: [
          "–",
          " ",
          formatMinorUnits(item.outstandingBalance, {
            currency: item.currency,
            locale: language
          })
        ] })
      }
    ) : /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Heading, { align: "left", level: theme === "apac" ? "3" : "4", children: t("remaining balance...") }),
        right: /* @__PURE__ */ jsx(RightAlignedText, { size: "large", weight: "strong", children: formatMinorUnits(item.remainingBalance, {
          currency: item.currency,
          locale: language
        }) })
      }
    )
  ] }) });
};
const ContractBalance = () => {
  const {
    orderSummary: { contractBalanceItems },
    padding,
    prefixContent
  } = useOrderSummaryPresentationData();
  const { t } = useTranslations(translations);
  assert(
    (contractBalanceItems == null ? void 0 : contractBalanceItems.length) > 0,
    "contractBalanceItems shouldn't be undefined or empty"
  );
  return /* @__PURE__ */ jsx(
    OrderSummaryLayout,
    {
      paddingStyles: padding,
      title: t("your ad budget..."),
      prefixContent: prefixContent == null ? void 0 : prefixContent.contractBalance,
      children: /* @__PURE__ */ jsx(Stack, { space: "large", children: /* @__PURE__ */ jsx(Stack, { space: "large", children: contractBalanceItems.map((balanceItem, index) => /* @__PURE__ */ jsxs(Fragment, { children: [
        index > 0 ? /* @__PURE__ */ jsx(Divider, {}) : null,
        /* @__PURE__ */ jsx(ContractBalanceItem, { item: balanceItem }, index)
      ] })) }) })
    }
  );
};
const PayablesSummary = () => {
  var _a;
  const {
    orderSummary: {
      payableLineItems,
      subTotal,
      surcharge,
      tax,
      total,
      currency
    },
    padding,
    language
  } = useOrderSummaryPresentationData();
  const { t } = useTranslations(translations);
  assert(
    payableLineItems.length > 0,
    "show payable summary only when there is payable line item"
  );
  const isAmountAboveZero = subTotal > 0 || ((_a = tax == null ? void 0 : tax.amount) == null ? void 0 : _a.value) && tax.amount.value > 0 || surcharge > 0;
  return /* @__PURE__ */ jsx(
    OrderSummaryLayout,
    {
      paddingStyles: padding,
      title: t("order summary..."),
      prefixContent: /* @__PURE__ */ jsx(OrderSummaryPrefixContent, {}),
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(PayableHeader, {}),
          /* @__PURE__ */ jsx(Divider, { weight: "strong" })
        ] }),
        payableLineItems.map((item) => /* @__PURE__ */ jsx(
          ProductLineItem,
          {
            lineItem: item,
            language,
            showPartial: true
          },
          item.title
        )),
        isAmountAboveZero && /* @__PURE__ */ jsx(
          SubTotal,
          {
            subTotal,
            tax,
            surcharge,
            language,
            currency
          }
        ),
        /* @__PURE__ */ jsx(
          Total,
          {
            total,
            tax,
            language,
            currency
          }
        )
      ] })
    }
  );
};
const OrderSummaryPrefixContent = () => {
  const { t } = useTranslations(translations);
  const {
    orderSummary: { payableLineItems },
    prefixContent
  } = useOrderSummaryPresentationData();
  const payingProductLineItem = payableLineItems.find(
    (lineItem) => lineItem.state === "outstanding"
  );
  return /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    prefixContent == null ? void 0 : prefixContent.payablesSummary,
    payingProductLineItem && /* @__PURE__ */ jsx(AlertMessage, { message: t(alertMessageResponses.costThisInvoice) })
  ] });
};
const OrderSummary = () => {
  const {
    orderSummary: { contractBalanceItems, total },
    space = "xlarge"
  } = useOrderSummaryPresentationData();
  const { trackEventHandler, orderSummary: contextOrderSummary } = useContext(
    OrderSummaryDataContext
  );
  const orderId = contextOrderSummary == null ? void 0 : contextOrderSummary.orderId;
  const showContractBalanceItems = (contractBalanceItems == null ? void 0 : contractBalanceItems.length) > 0;
  const showPayablesSummary = total > 0;
  useEffect(() => {
    if (orderId && (showContractBalanceItems || showPayablesSummary)) {
      trackEventHandler(
        "order_summary_displayed",
        {
          orderId
        },
        "view"
      );
    }
  }, [
    orderId,
    trackEventHandler,
    showContractBalanceItems,
    showPayablesSummary
  ]);
  return /* @__PURE__ */ jsxs(Stack, { space, children: [
    showContractBalanceItems && /* @__PURE__ */ jsx(ContractBalance, {}),
    showPayablesSummary && /* @__PURE__ */ jsx(PayablesSummary, {})
  ] });
};
const OrderSummaryPresentation = () => {
  const { cpqaOrderSummary } = useOrderSummaryPresentationData();
  return cpqaOrderSummary ? /* @__PURE__ */ jsx(CPQASummary, {}) : /* @__PURE__ */ jsx(OrderSummary, {});
};
const HirerOrderSummary = (props) => /* @__PURE__ */ jsx(LoggerProvider, { context: props.logger || {}, children: /* @__PURE__ */ jsx(OrderSummaryDataProvider, { ...props, children: /* @__PURE__ */ jsx(OrderSummaryPresentation, {}) }) });
const useShoppingCartContext = () => {
  const shoppingCartContext = useContext(ShoppingCartContext);
  if (!shoppingCartContext) {
    throw new Error("ShoppingCartContext is not provided");
  }
  return shoppingCartContext;
};
const useShoppingCart = () => {
  const shoppingCartContext = useContext(ShoppingCartContext);
  if (!shoppingCartContext) {
    throw new Error("ShoppingCartContext is not provided");
  }
  if (!shoppingCartContext.cartState) {
    throw new Error("Cart is not loaded");
  }
  return {
    ...shoppingCartContext,
    cartState: shoppingCartContext.cartState
  };
};
const useHirerAccount = (logger) => {
  const { data, loading } = useHirerAccountQuery({
    onError: (error) => {
      logger.error("Checkout | HirerAccountQuery", {
        error
      });
    }
  });
  return {
    loading,
    hirerAccount: data == null ? void 0 : data.hirerAccount
  };
};
const defaultLocale = "en-AU";
const useLocale = () => {
  const { locale } = useLanguage();
  if (!locale) {
    return defaultLocale;
  }
  const isMelwaysLocale = (input) => locales.includes(locale);
  return isMelwaysLocale() ? locale : defaultLocale;
};
const PriceChangeAlert = () => {
  const { t } = useTranslations(translations$1);
  return /* @__PURE__ */ jsx(Alert, { tone: "info", children: /* @__PURE__ */ jsx(Text, { children: t("price-changed-notice-text-content") }) });
};
const OrderSummaryController = (props) => {
  const locale = useLocale();
  const { hirerAccount, loading } = useHirerAccount(props.logger);
  const { orderState } = useShoppingCart();
  const theme = useThemeName();
  if (loading || (hirerAccount == null ? void 0 : hirerAccount.hidePrice) === true) {
    return /* @__PURE__ */ jsx(Bleed, { top: "gutter", children: /* @__PURE__ */ jsx(Fragment, {}) });
  }
  const includePayablesSummary = orderState.productAlert === "SELECTED_PRODUCT_PRICE_CHANGED";
  const onTrackEventHandler = (eventName, eventData, type = "view") => {
    props.onTrackingEvent({
      tracking: {
        eventName,
        eventData,
        type
      },
      hubble: {
        eventName,
        eventData
      }
    });
  };
  return /* @__PURE__ */ jsx(
    HirerOrderSummary,
    {
      hubbleTrackEventHandler: onTrackEventHandler,
      language: locale,
      logger: props.logger,
      orderId: props.orderId,
      surcharge: props.surcharge,
      styles: {
        padding: {
          paddingTop: theme === "apac" ? "large" : "none",
          paddingBottom: theme === "apac" ? "large" : "none",
          paddingX: theme === "apac" ? "gutter" : void 0
        },
        space: "medium"
      },
      prefixContent: {
        payablesSummary: includePayablesSummary ? /* @__PURE__ */ jsx(PriceChangeAlert, {}) : void 0
      }
    }
  );
};
const getCheckoutPaymentResult = ({
  paymentResult,
  orderNumber: inputOrderNumber,
  promoCode,
  totalOrderAmountPayable,
  paymentMethod
}) => {
  const orderNumber = inputOrderNumber ?? "";
  return {
    ...paymentResult,
    paymentMethod: paymentResult.paymentMethod ?? paymentMethod,
    productTotalPrice: totalOrderAmountPayable ?? 0,
    discountCode: promoCode ?? "",
    orderNumber
  };
};
const calculateTotalOrderAmountPayable = (orderSummary) => {
  if (!orderSummary) {
    return void 0;
  }
  return orderSummary.lineItems.reduce(
    (acc, current) => acc + current.price.netPrice.value,
    0
  );
};
const isBillToAgent = (hirerAccount) => {
  if (!hirerAccount) {
    return false;
  }
  return (hirerAccount.hasAgent ?? false) && hirerAccount.billingStatus === "OK";
};
const isInvoiceOnly = (contractBalances) => contractBalances !== void 0 && contractBalances.length > 0;
const isPayingWithBudget = (contractBalances, totalOrderAmountPayable) => {
  if (totalOrderAmountPayable === void 0 || contractBalances === void 0) {
    return false;
  }
  const totalRemaining = contractBalances.reduce(
    (acc, contract) => acc + contract.totalValue.dollars.remaining.value,
    0
  );
  return totalRemaining >= totalOrderAmountPayable;
};
const isPaymentDetailsHidden = (payingWithBudget, totalOrderAmountPayable, hidePrice, isPerUnitBilling2 = false) => {
  const isFreeProduct = Boolean(Number(totalOrderAmountPayable) === 0);
  if (isPerUnitBilling2) {
    return false;
  }
  return Boolean(hidePrice || isFreeProduct || payingWithBudget);
};
const isPaymentRequired = (totalOrderAmountPayable, payingWithBudget) => Number(totalOrderAmountPayable) > 0 && !payingWithBudget;
const isPerUnitBilling = (orderSummary) => {
  var _a;
  const result = (_a = orderSummary == null ? void 0 : orderSummary.lineItems) == null ? void 0 : _a.find(
    (item) => {
      var _a2;
      return ((_a2 = item.pricing) == null ? void 0 : _a2.__typename) === "CappedUnitPrice";
    }
  );
  return Boolean(result);
};
const getPaymentFieldsFromData = ({
  contractBalances,
  hirerAccount,
  orderSummary
}) => {
  const billToAgent = isBillToAgent(hirerAccount);
  const invoiceOnly = isInvoiceOnly(contractBalances);
  const totalOrderAmountPayable = calculateTotalOrderAmountPayable(orderSummary);
  const payingWithBudget = isPayingWithBudget(
    contractBalances,
    totalOrderAmountPayable
  );
  const hideAllPayments = isPaymentDetailsHidden(
    payingWithBudget,
    totalOrderAmountPayable,
    (hirerAccount == null ? void 0 : hirerAccount.hidePrice) ?? false,
    isPerUnitBilling(orderSummary)
  );
  const requiresPayment = isPaymentRequired(
    totalOrderAmountPayable,
    payingWithBudget
  );
  return {
    billToAgent,
    hideAllPayments,
    invoiceOnly,
    payingWithBudget,
    requiresPayment,
    totalOrderAmountPayable
  };
};
const useContractBalances = (logger) => {
  const { data, loading } = useGetContractBalancesQuery({
    variables: {
      advertiserNation: "test-advertiser-nation"
      // TODO: needs removal
    },
    onError: (error) => {
      logger.error("Checkout | GetContractBalancesQuery", {
        error
      });
    },
    fetchPolicy: "network-only"
  });
  return {
    loading,
    contractBalances: data == null ? void 0 : data.contractBalances
  };
};
const useOrderSummary = ({
  orderId,
  logger
}) => {
  const { data, loading } = useGetOrderSummaryQuery({
    variables: {
      orderId
    },
    skip: !orderId,
    onError: (error) => {
      logger.error("Checkout | GetOrderSummaryQuery", {
        error
      });
    }
  });
  return {
    loading,
    orderSummary: data == null ? void 0 : data.orderSummary
  };
};
const usePaymentsData = (orderId, logger) => {
  const { loading: contractBalancesLoading, contractBalances } = useContractBalances(logger);
  const { loading: hirerAccountLoading, hirerAccount } = useHirerAccount(logger);
  const { loading: orderSummaryLoading, orderSummary } = useOrderSummary({
    orderId,
    logger
  });
  return {
    loading: contractBalancesLoading || hirerAccountLoading || orderSummaryLoading,
    data: {
      contractBalances,
      hirerAccount,
      orderSummary
    }
  };
};
const isAdBudgetSelected = (cartState) => {
  var _a;
  const result = (_a = cartState.items) == null ? void 0 : _a.find(
    (item) => item.product.__typename === "AdBudgetCartItem"
  );
  return Boolean(result);
};
const isOrderExpiredError = (error) => {
  const isKnownError = "errorCode" in error;
  if (!isKnownError) {
    return false;
  }
  const errorCode = error.errorCode;
  return errorCode === "order_expired";
};
const createCheckoutWidgetEvent = ({
  hideAllPayments,
  hirerNation,
  hasSufficientBudget,
  requiresPayment
}) => ({
  tracking: {
    type: "link",
    eventName: "checkout_widget",
    paymentMethodShown: hideAllPayments ? "not shown" : getSupportedPaymentMethodsForCaja(hirerNation.toLowerCase()),
    hasSufficientBudget,
    isPayment: requiresPayment
  }
});
const DEFAULT_PAYMENT_METHOD = "CreditCard";
const PaymentsController = (props) => {
  var _a;
  const { loading, data } = usePaymentsData(props.orderId, props.logger);
  const { cartState, createOrder, orderState } = useShoppingCart();
  const isAdBudget = isAdBudgetSelected(cartState);
  const {
    billToAgent,
    hideAllPayments,
    invoiceOnly,
    payingWithBudget,
    requiresPayment,
    totalOrderAmountPayable
  } = getPaymentFieldsFromData({
    contractBalances: data.contractBalances,
    hirerAccount: data.hirerAccount,
    orderSummary: data.orderSummary
  });
  const determineInitialPaymentMethod = () => invoiceOnly || hideAllPayments ? "Invoice" : DEFAULT_PAYMENT_METHOD;
  const [paymentMethodSelected, setPaymentMethodSelected] = useState(determineInitialPaymentMethod());
  const checkoutOnPaymentMethodChanged = (_selectedIndex, selectedItem) => {
    if (!selectedItem) {
      return;
    }
    setPaymentMethodSelected(selectedItem);
    if (props.callbacks.onPaymentMethodChanged) {
      props.callbacks.onPaymentMethodChanged(_selectedIndex, selectedItem);
    }
  };
  const checkoutOnPaymentFailed = (error) => {
    if (isOrderExpiredError(error)) {
      props.logger.warn("Checkout | PaymentsController | Order expired", {
        err: error
      });
      createOrder();
    } else {
      props.logger.error("Checkout | PaymentsController | onPaymentFailed", {});
      return props.callbacks.onPaymentFailed(error);
    }
  };
  const checkoutOnPaymentSuccess = (result) => {
    var _a2;
    if (orderState.order === void 0) {
      props.logger.warn(
        "Checkout | PaymentsController | onPaymentSuccess | Order not found",
        {
          cart: cartState,
          order: orderState
        }
      );
    }
    if (totalOrderAmountPayable === void 0) {
      props.logger.warn(
        "Checkout | PaymentsController | onPaymentSuccess | totalOrderAmountPayable not found",
        {
          cart: cartState,
          order: orderState
        }
      );
    }
    const checkoutPaymentResult = getCheckoutPaymentResult({
      paymentResult: result,
      orderNumber: (_a2 = orderState.order) == null ? void 0 : _a2.orderNumber,
      promoCode: cartState.promoCode,
      totalOrderAmountPayable,
      paymentMethod: paymentMethodSelected
    });
    return props.callbacks.onPaymentSuccess(checkoutPaymentResult);
  };
  const trackLink = (eventName, eventAttributes) => {
    props.callbacks.onTrackingEvent({
      tracking: {
        type: "link",
        eventName,
        ...eventAttributes
      }
    });
  };
  useEffect(() => {
    var _a2;
    if (((_a2 = data.hirerAccount) == null ? void 0 : _a2.nation) && data.contractBalances && data.orderSummary) {
      props.callbacks.onTrackingEvent(
        createCheckoutWidgetEvent({
          hideAllPayments,
          hirerNation: data.hirerAccount.nation,
          hasSufficientBudget: payingWithBudget,
          requiresPayment
        })
      );
    }
  }, [data.contractBalances, data.orderSummary, (_a = data.hirerAccount) == null ? void 0 : _a.nation]);
  if (loading || !data.orderSummary) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    PaymentsForCaja,
    {
      backButtonComponent: props.backButtonComponent,
      billToAgent,
      cartId: cartState.id,
      currency: data.orderSummary.total.currency,
      env: props.env,
      handledCancelPayment: props.callbacks.handleCancelledPayment,
      hideAllPayments,
      idempotencyKey: props.idempotencyKey,
      invoiceOnly,
      isAdBudgetSelected: isAdBudget,
      onError: checkoutOnPaymentFailed,
      onPaymentMethodChanged: checkoutOnPaymentMethodChanged,
      onPostAdButtonClicked: props.callbacks.onCheckoutButtonClicked,
      onPostAdButtonClickFailed: props.callbacks.onCheckoutButtonClickedFailed,
      onReplaceSavedCard: props.callbacks.onReplaceSavedCard,
      onSaveCardOptionChanged: props.callbacks.onSaveCardOptionChanged,
      onSavedCardLoaded: props.callbacks.onSavedCardLoaded,
      onSuccess: checkoutOnPaymentSuccess,
      orderId: props.orderId,
      paymentMethod: paymentMethodSelected,
      postAdButtonText: props.postAdButtonText,
      previewButtonComponent: props.previewButtonComponent,
      site: props.site,
      trackLink,
      useCase: props.useCase,
      onGetSurchargeInfo: props.onGetSurchargeInfo
    }
  );
};
const HooraySvg = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "90",
    height: "91",
    viewBox: "0 0 90 91",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M38.7332 39.5709C36.8422 38.8419 34.7092 39.7579 33.9672 41.6159L30.3902 50.1109C29.6522 51.9609 29.5392 52.2199 27.9862 52.3769C16.9302 53.4939 12.4572 66.3019 12.4572 73.4329V81.8229C12.4572 86.6109 14.0842 89.0699 19.4212 89.0699C21.9662 89.0699 24.7632 86.6109 24.7632 81.8229V71.2239C26.6552 70.3959 28.4212 69.3049 30.0342 67.9109C30.0422 67.9059 30.0492 67.9009 30.0572 67.8959C30.0562 67.8949 30.0562 67.8939 30.0552 67.8929C32.9772 65.3609 35.3902 61.8249 37.1032 57.0069L40.8142 44.2549C41.5552 42.3959 40.6252 40.2999 38.7332 39.5709Z",
          fill: "#D6EDF7"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M46.825 10.9857C46.753 10.9857 46.68 10.9737 46.608 10.9477C46.27 10.8287 46.093 10.4577 46.212 10.1187L49.631 0.433748C49.751 0.0947485 50.12 -0.0802516 50.461 0.0377484C50.799 0.156748 50.976 0.527748 50.857 0.866748L47.438 10.5517C47.344 10.8187 47.093 10.9857 46.825 10.9857Z",
          fill: "#2666CF"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M40.672 10.9859C40.404 10.9859 40.153 10.8189 40.058 10.5519L36.64 0.86685C36.521 0.52785 36.699 0.15685 37.037 0.0378499C37.374 -0.0831501 37.747 0.0958499 37.866 0.43385L41.285 10.1188C41.404 10.4578 41.226 10.8288 40.888 10.9479C40.816 10.9739 40.743 10.9859 40.672 10.9859Z",
          fill: "#2666CF"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M54.346 15.1359C54.201 15.1359 54.054 15.0879 53.933 14.9879C53.656 14.7599 53.617 14.3499 53.845 14.0729L60.681 5.77195C60.91 5.49395 61.319 5.45495 61.596 5.68295C61.874 5.91095 61.913 6.32095 61.685 6.59795L54.848 14.8999C54.72 15.0559 54.534 15.1359 54.346 15.1359Z",
          fill: "#2666CF"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M32.81 15.1359C32.622 15.1359 32.436 15.0559 32.307 14.8999L25.47 6.59795C25.242 6.32095 25.282 5.91095 25.559 5.68295C25.836 5.45495 26.245 5.49395 26.474 5.77195L33.311 14.0729C33.539 14.3499 33.5 14.7599 33.222 14.9879C33.101 15.0879 32.955 15.1359 32.81 15.1359Z",
          fill: "#2666CF"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M14.833 71.6258C14.474 71.6258 14.183 71.3348 14.183 70.9758C14.183 70.6168 14.474 70.3258 14.833 70.3258C25.873 70.3258 32.938 65.4098 36.431 55.2968L39.815 43.3248C40.383 41.8518 39.692 40.2498 38.297 39.6948C37.627 39.4298 36.897 39.4428 36.236 39.7308C35.566 40.0238 35.05 40.5638 34.783 41.2528L31.507 49.2628C30.442 52.0118 28.915 51.9538 28.475 51.8738C27.508 51.6928 26.882 50.7318 26.882 49.4238V45.1028C26.882 44.7438 27.173 44.4528 27.532 44.4528C27.891 44.4528 28.182 44.7438 28.182 45.1028V49.4238C28.182 50.1608 28.458 50.5478 28.714 50.5958C29.052 50.6578 29.74 50.2248 30.299 48.7818L33.575 40.7718C33.964 39.7678 34.726 38.9718 35.716 38.5388C36.696 38.1118 37.783 38.0928 38.775 38.4868C40.833 39.3028 41.851 41.6578 41.047 43.7358L37.671 55.6858C33.963 66.4228 26.496 71.6258 14.833 71.6258Z",
          fill: "#2666CF"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M17.691 87.5798C12.884 87.5798 8.973 83.6258 8.973 78.7658V71.4128C3.015 68.8568 -0.00299777 61.4388 2.23437e-06 49.3518V30.3958C2.23437e-06 28.1608 1.801 26.3428 4.014 26.3428C5.048 26.3428 5.991 26.7388 6.703 27.3898V24.1998C6.703 21.9648 8.503 20.1478 10.716 20.1478C11.761 20.1478 12.714 20.5528 13.429 21.2158V20.8098C13.429 18.5748 15.229 16.7568 17.442 16.7568C19.303 16.7568 20.871 18.0418 21.324 19.7788C22.051 19.0408 23.057 18.5828 24.168 18.5828C26.382 18.5828 28.182 20.3998 28.182 22.6338V27.9438C28.182 28.3028 27.891 28.5938 27.532 28.5938C27.173 28.5938 26.882 28.3028 26.882 27.9438V22.6338C26.882 21.1168 25.665 19.8818 24.168 19.8818C22.672 19.8818 21.455 21.1168 21.455 22.6338V44.3318C21.455 44.6908 21.164 44.9818 20.805 44.9818C20.446 44.9818 20.155 44.6908 20.155 44.3318V20.8098C20.155 19.2918 18.938 18.0568 17.442 18.0568C15.946 18.0568 14.729 19.2918 14.729 20.8098V45.1028C14.729 45.4618 14.438 45.7528 14.079 45.7528C13.72 45.7528 13.429 45.4618 13.429 45.1028V24.1998C13.429 22.6818 12.212 21.4468 10.716 21.4468C9.221 21.4468 8.003 22.6818 8.003 24.1998V29.9518C8.019 30.0968 8.027 30.2458 8.027 30.3958V34.1548C8.027 34.2158 8.019 34.2748 8.003 34.3308V46.8798C8.003 47.2378 7.712 47.5288 7.353 47.5288C6.994 47.5288 6.703 47.2378 6.703 46.8798V30.0288C6.526 28.6838 5.387 27.6428 4.014 27.6428C2.517 27.6428 1.3 28.8778 1.3 30.3958V49.3518C1.297 61.1148 4.171 68.1838 9.842 70.3628C9.88 70.3768 9.917 70.3948 9.952 70.4158C10.036 70.4648 10.106 70.5308 10.159 70.6078C10.212 70.6848 10.248 70.7738 10.264 70.8688C10.271 70.9088 10.274 70.9498 10.273 70.9898V78.7658C10.273 82.9088 13.601 86.2798 17.691 86.2798C21.782 86.2798 25.109 82.9088 25.109 78.7658V74.2348C25.109 73.8768 25.4 73.5858 25.759 73.5858C26.118 73.5858 26.409 73.8768 26.409 74.2348V78.7658C26.409 83.6258 22.498 87.5798 17.691 87.5798Z",
          fill: "#2666CF"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M47.6737 39.5707C49.5647 38.8417 51.6967 39.7577 52.4397 41.6157L56.0167 50.1107C56.7547 51.9607 56.8597 52.3767 58.4207 52.3767C67.0167 52.3767 68.9157 61.2297 72.6237 63.0297C83.7597 68.4357 83.0487 45.3117 83.0487 45.3117V27.5777C83.6887 27.0447 84.5147 26.7207 85.4187 26.7207C87.4487 26.7207 89.0967 28.3387 89.0967 30.3347V50.3347C89.0967 54.5997 89.2007 69.7477 79.2867 73.4327V81.8227C79.2867 86.6107 75.3367 90.4927 70.4657 90.4927C65.5937 90.4927 61.6447 86.6107 61.6447 81.8227V71.2237C59.7517 70.3957 57.9857 69.3047 56.3727 67.9107C56.3637 67.9057 56.3577 67.9007 56.3497 67.8957C56.3507 67.8947 56.3507 67.8937 56.3517 67.8927C53.4297 65.3607 51.0167 61.8247 49.3037 57.0067L45.5927 44.2547C44.8517 42.3957 45.7817 40.2997 47.6737 39.5707Z",
          fill: "#D6EDF7"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M72.2784 71.6337C60.2084 71.6337 52.4804 66.4347 48.6514 55.7397L45.1224 43.6977C44.7424 42.7587 44.7594 41.6647 45.1904 40.6777C45.6304 39.6717 46.4464 38.8977 47.4874 38.4987C49.6024 37.6857 52.0034 38.7157 52.8384 40.7887L56.2234 48.7747C56.7774 50.1537 57.4944 50.6897 57.9094 50.6057C58.2404 50.5427 58.4704 50.0617 58.4704 49.4367V45.1157C58.4704 44.7567 58.7614 44.4657 59.1214 44.4657C59.4804 44.4657 59.7714 44.7567 59.7714 45.1157V49.4367C59.7714 50.7327 59.1374 51.6927 58.1564 51.8827C57.2244 52.0617 55.9074 51.4737 55.0214 49.2707L51.6364 41.2857C51.0624 39.8547 49.4144 39.1547 47.9524 39.7127C47.2394 39.9857 46.6804 40.5137 46.3814 41.1987C46.0904 41.8637 46.0804 42.6007 46.3494 43.2737L49.8874 55.3387C53.4974 65.4167 60.8244 70.3337 72.2784 70.3337C72.6374 70.3337 72.9284 70.6247 72.9284 70.9837C72.9284 71.3427 72.6374 71.6337 72.2784 71.6337Z",
          fill: "#2666CF"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M69.3184 87.5847C64.3504 87.5847 60.3084 83.6317 60.3084 78.7727V74.2437C60.3084 73.8847 60.5994 73.5937 60.9584 73.5937C61.3184 73.5937 61.6094 73.8847 61.6094 74.2437V78.7727C61.6094 82.9147 65.0674 86.2847 69.3184 86.2847C73.5704 86.2847 77.0284 82.9147 77.0284 78.7727V70.9977C77.0284 70.9597 77.0304 70.9217 77.0364 70.8847V70.8827C77.0514 70.7827 77.0894 70.6917 77.1454 70.6127C77.1994 70.5337 77.2714 70.4677 77.3574 70.4187C77.3914 70.3987 77.4284 70.3827 77.4664 70.3687C83.3474 68.1887 86.3274 61.1227 86.3244 49.3647V30.4117C86.3244 28.8937 85.0524 27.6587 83.4894 27.6587C82.0534 27.6587 80.8634 28.7017 80.6804 30.0497V46.8927C80.6804 47.2517 80.3894 47.5427 80.0304 47.5427C79.6704 47.5427 79.3794 47.2517 79.3794 46.8927V34.3477C79.3644 34.2917 79.3554 34.2327 79.3554 34.1707V30.4117C79.3554 30.2617 79.3644 30.1147 79.3794 29.9697V24.2167C79.3794 22.6987 78.1084 21.4647 76.5454 21.4647C74.9824 21.4647 73.7104 22.6987 73.7104 24.2167V45.1157C73.7104 45.4747 73.4194 45.7657 73.0604 45.7657C72.7014 45.7657 72.4104 45.4747 72.4104 45.1157V20.8267C72.4104 19.3087 71.1384 18.0747 69.5754 18.0747C68.0124 18.0747 66.7424 19.3087 66.7424 20.8267V44.3447C66.7424 44.7037 66.4514 44.9947 66.0914 44.9947C65.7324 44.9947 65.4414 44.7037 65.4414 44.3447V22.6527C65.4414 21.1347 64.1694 19.8997 62.6064 19.8997C61.0424 19.8997 59.7714 21.1347 59.7714 22.6527V27.9607C59.7714 28.3197 59.4804 28.6107 59.1214 28.6107C58.7614 28.6107 58.4704 28.3197 58.4704 27.9607V22.6527C58.4704 20.4177 60.3264 18.5997 62.6064 18.5997C63.7664 18.5997 64.8174 19.0707 65.5694 19.8277C66.0234 18.0747 67.6474 16.7747 69.5754 16.7747C71.8554 16.7747 73.7104 18.5927 73.7104 20.8267V21.2697C74.4514 20.5847 75.4494 20.1647 76.5454 20.1647C78.8254 20.1647 80.6804 21.9827 80.6804 24.2167V27.4407C81.4174 26.7697 82.4054 26.3587 83.4894 26.3587C85.7694 26.3587 87.6244 28.1767 87.6244 30.4117V49.3647C87.6274 61.4567 84.4994 68.8767 78.3294 71.4277V78.7727C78.3294 83.6317 74.2874 87.5847 69.3184 87.5847Z",
          fill: "#2666CF"
        }
      )
    ]
  }
);
const PromoCodeSuccessAlert = ({
  promoCode,
  free,
  onTrackingEvent
}) => {
  const { t } = useTranslations(translations$2);
  const successAlertText = t("Success! Your code", { promoCode });
  useEffect(() => {
    onTrackingEvent({
      tracking: {
        type: "link",
        eventName: "alert_viewed",
        data: {
          currentPageSection: "promo code",
          alertShown: [
            {
              alertShownTitle: "apply promo code success",
              alertShownMessageCode: [successAlertText]
            }
          ]
        }
      }
    });
  }, []);
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Alert, { tone: "positive", children: /* @__PURE__ */ jsx(Text, { children: successAlertText }) }),
    free && /* @__PURE__ */ jsx(
      Box,
      {
        background: "infoLight",
        borderRadius: "standard",
        "data-testid": "zero-balance",
        padding: "medium",
        children: /* @__PURE__ */ jsxs(Columns, { alignY: "center", collapseBelow: "tablet", space: "medium", children: [
          /* @__PURE__ */ jsx(Column, { width: "1/5", children: /* @__PURE__ */ jsx(HooraySvg, {}) }),
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsx(Heading, { level: "3", children: t("This ad's on us!") }),
            /* @__PURE__ */ jsx(Text, { children: t("Great news! Your Ad is completely free...") })
          ] }) })
        ] })
      }
    )
  ] });
};
const PromoCodeSuccessAlertController = (props) => {
  const { cartState } = useShoppingCart();
  const { orderSummary, loading } = useOrderSummary({
    orderId: props.orderId,
    logger: props.logger
  });
  const totalOrderAmountPayable = calculateTotalOrderAmountPayable(orderSummary);
  if (!cartState.promoCode || loading) return null;
  return /* @__PURE__ */ jsx(
    PromoCodeSuccessAlert,
    {
      promoCode: cartState.promoCode,
      free: totalOrderAmountPayable === 0,
      onTrackingEvent: props.onTrackingEvent
    }
  );
};
const OrderSummaryAndPaymentsController = (props) => {
  const { orderState } = useShoppingCart();
  const { order, loading } = orderState;
  const [surcharge, setSurcharge] = useState(void 0);
  const getSurchargeInfo = useCallback(
    (surchargeBrand, surchargePercentage, surchargeApplicable) => {
      setSurcharge({
        surchargeBrand,
        surchargePercentage,
        surchargeApplicable
      });
    },
    [setSurcharge]
  );
  if (loading === true) {
    return /* @__PURE__ */ jsx(Loader, {});
  }
  if (!(order == null ? void 0 : order.orderId)) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
    /* @__PURE__ */ jsx(
      PromoCodeSuccessAlertController,
      {
        orderId: order.orderId,
        logger: props.logger,
        onTrackingEvent: props.callbacks.onTrackingEvent
      }
    ),
    /* @__PURE__ */ jsx(
      OrderSummaryController,
      {
        orderId: order.orderId,
        onTrackingEvent: props.callbacks.onTrackingEvent,
        logger: props.logger,
        surcharge
      }
    ),
    /* @__PURE__ */ jsx(
      PaymentsController,
      {
        ...props,
        onGetSurchargeInfo: getSurchargeInfo,
        orderId: order.orderId
      }
    )
  ] });
};
const getProductIdForPromoCode = (cartItems, logger) => {
  if (cartItems.length === 0) {
    logger.error(
      "Cart items are empty, using empty string as productId for HirerPromoCodesInput",
      {}
    );
    return "";
  }
  const ROOT_PRODUCT_REFERENCE_ID = "product|parent";
  const parentItem = cartItems.find(
    (item) => item.referenceId === ROOT_PRODUCT_REFERENCE_ID
  );
  if (!parentItem) {
    return cartItems[0].product.id;
  }
  return parentItem.product.id;
};
const PromoCodeBanner = (props) => {
  const { cartState } = useShoppingCart();
  const selectedProductId = getProductIdForPromoCode(
    cartState.items,
    props.logger
  );
  return /* @__PURE__ */ jsx(
    HirerPromoCodesBanner,
    {
      productIds: [selectedProductId],
      padding: { paddingX: "medium", paddingY: "medium" }
    }
  );
};
const isPromoCodeInputShown = ({
  cartItems,
  totalOrderAmountPayable,
  cartPromoCode
}) => {
  const selectedAdBudget = cartItems.find(
    (item) => item.product.__typename === "AdBudgetCartItem"
  );
  const promoCodeSuccessfullyApplied = cartPromoCode !== void 0;
  const isPaymentRequired2 = Number(totalOrderAmountPayable) > 0;
  const canApplyPromoCode = isPaymentRequired2 || promoCodeSuccessfullyApplied;
  return !selectedAdBudget && canApplyPromoCode;
};
const PromoCodeController = (props) => {
  var _a;
  const { cartState, onApplyPromoCode, onClearPromoCode, orderState } = useShoppingCart();
  const productId = getProductIdForPromoCode(cartState.items, props.logger);
  const { orderSummary } = useOrderSummary({
    orderId: (_a = orderState.order) == null ? void 0 : _a.orderId,
    logger: props.logger
  });
  const { shouldRender: shouldRenderPromoCodesBanner } = usePromoCodesBanner([
    productId
  ]);
  const totalOrderAmountPayable = calculateTotalOrderAmountPayable(orderSummary);
  if (!isPromoCodeInputShown({
    cartItems: cartState.items,
    totalOrderAmountPayable,
    cartPromoCode: cartState.promoCode ?? void 0
  })) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    HirerPromoCodesInput,
    {
      productId,
      bannerToDisplay: shouldRenderPromoCodesBanner ? /* @__PURE__ */ jsx(PromoCodeBanner, { logger: props.logger }) : void 0,
      onSuccess: (promoCode) => {
        onApplyPromoCode(promoCode);
        props.onTrackingEvent({
          tracking: {
            type: "link",
            eventName: "apply_promo_code_succeeded",
            eventType: "apply_promo_code_succeeded",
            currentPageSection: "promo code",
            discountCode: promoCode
          }
        });
      },
      onClear: () => {
        onClearPromoCode();
      },
      onError: (error) => props.onTrackingEvent({
        tracking: {
          type: "link",
          eventName: "form_validation_error_displayed",
          eventType: "apply_promo_code_failed",
          currentPageSection: "promo code",
          errorMessage: error.message ?? "Error applying promo code",
          discountCode: cartState.promoCode ?? "",
          validationType: "field"
        }
      }),
      onInvalid: (_message, discountCode) => props.onTrackingEvent({
        tracking: {
          type: "link",
          eventName: "form_validation_error_displayed",
          eventType: "apply_promo_code_failed",
          currentPageSection: "promo code",
          errorMessage: "Error applying promo code",
          discountCode,
          validationType: "field"
        }
      }),
      onClick: (discountCode) => props.onTrackingEvent({
        tracking: {
          type: "link",
          eventName: "apply_promo_code_pressed",
          eventType: "apply_promo_code_pressed",
          currentPageSection: "promo code",
          discountCode
        }
      })
    }
  );
};
const CreateOrderError = () => {
  const { t } = useTranslations(translations$1);
  return /* @__PURE__ */ jsx(Alert, { tone: "critical", children: /* @__PURE__ */ jsxs(Text, { children: [
    /* @__PURE__ */ jsxs("b", { children: [
      " ",
      t("checkout-page-error-title")
    ] }),
    /* @__PURE__ */ jsx("p", { children: t("checkout-page-error-text-content") })
  ] }) });
};
const CheckoutView = ({
  backButtonComponent,
  callbacks,
  env,
  idempotencyKey,
  logger,
  postAdButtonText,
  previewButtonComponent,
  site,
  useCase,
  fatalError,
  isLoading
}) => {
  const {
    handleCancelledPayment,
    onPostAdButtonClick,
    onPostAdButtonClickFailed,
    onPaymentFailed,
    onPaymentSuccess,
    handlePaymentOptionChange,
    onReplaceSavedCard,
    onSaveCardOptionChanged,
    onSavedCardLoaded,
    onTrackingEvent
  } = callbacks;
  if (isLoading) {
    return /* @__PURE__ */ jsx(Loader, {});
  }
  if (fatalError == null ? void 0 : fatalError.cartNotReady) {
    return null;
  }
  return /* @__PURE__ */ jsx(ContentBlock, { width: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
    /* @__PURE__ */ jsx(
      PromoCodeController,
      {
        onTrackingEvent,
        logger
      }
    ),
    (fatalError == null ? void 0 : fatalError.createOrderError) ? /* @__PURE__ */ jsx(CreateOrderError, {}) : null,
    !(fatalError == null ? void 0 : fatalError.createOrderError) && /* @__PURE__ */ jsx(
      OrderSummaryAndPaymentsController,
      {
        callbacks: {
          handleCancelledPayment,
          onCheckoutButtonClicked: onPostAdButtonClick,
          onCheckoutButtonClickedFailed: onPostAdButtonClickFailed,
          onPaymentFailed,
          onPaymentSuccess,
          onPaymentMethodChanged: handlePaymentOptionChange,
          onReplaceSavedCard,
          onSaveCardOptionChanged,
          onSavedCardLoaded,
          onTrackingEvent
        },
        backButtonComponent,
        env,
        idempotencyKey,
        logger,
        postAdButtonText,
        previewButtonComponent,
        site,
        useCase
      }
    )
  ] }) });
};
const defaultLogger = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const Checkout = (props) => {
  const logger = props.logger ?? defaultLogger;
  const { createOrder, orderState, cartState } = useShoppingCartContext();
  const orderRef = useRef(orderState.order);
  const isLoading = !cartState || orderState.loading;
  const fatalError = {
    createOrderError: orderState.createOrderFatalError,
    cartNotReady: !(cartState == null ? void 0 : cartState.isOrderReady)
  };
  const cartStateStringify = JSON.stringify(cartState);
  useEffect(() => {
    if (!cartStateStringify) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const isReturningLinkoutPaymentWithOrderId = typeof params.get("orderid") === "string";
    if (isReturningLinkoutPaymentWithOrderId) {
      return;
    }
    createOrder();
  }, [cartStateStringify, createOrder]);
  useEffect(() => {
    if (orderState.productAlert === "SELECTED_PRODUCT_UNAVAILABLE") {
      logger.warn("Selected Product Unavailable", { cartState });
      props.callbacks.notifyFatalError({
        reason: "product_unavailable"
      });
    }
  }, [orderState.productAlert]);
  useEffect(() => {
    if (fatalError == null ? void 0 : fatalError.createOrderError) {
      logger.error("Create Order Fatal Error", {
        message: fatalError.createOrderError
      });
      props.callbacks.notifyFatalError({
        reason: "create_order_fatal_error"
      });
    }
  }, [fatalError.createOrderError]);
  useEffect(() => {
    orderRef.current = orderState.order;
  }, [orderState.order]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (orderRef.current === void 0) {
        logger.info(
          "No order found, retriggering create order after 5 seconds",
          { orderState }
        );
        createOrder();
      }
    }, 5e3);
    return () => clearTimeout(timer);
  }, []);
  return /* @__PURE__ */ jsx(
    CheckoutView,
    {
      ...props,
      isLoading,
      fatalError,
      logger
    }
  );
};
export {
  Checkout,
  HirerContractBalance,
  HirerOrderSummary,
  Locales as Locale,
  checkIsPaymentsReturnUrl
};
